<script setup lang="ts">
/**
 * The url list component is used to display a list of urls so that the user can view and select them. It accepts a
 * list of urls and displays them in a table
 */
import { computed } from "vue";
import { TuButton } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import useTranslation from "@Composables/useTranslation.ts"
import { UrlAlias } from "@Resources/js/modules/urls/Data/Models/UrlAliasFactory.ts";
import emptyListImage1x from "@Images/teams/url-list-empty@1x.webp";
import emptyListImage2x from "@Images/teams/url-list-empty@2x.webp";
import emptyListImage3x from "@Images/teams/url-list-empty@3x.webp";
import XOverlay from "@Modules/shared/store/XOverlay";

import UrlListItem from "./UrlListItem.vue";

export interface Props {
  items?: UrlAlias[],
  loading?: boolean,
  selected?: string | null,
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  loading: false,
  selected: null,
});

interface Events {
  (e: "url-selected"): void;
  (e: "tag", name: string): void;
}

const emit = defineEmits<Events>();

//
// Setup
const overlay = XOverlay();
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Computed
const hasUrls = computed(() => props.items.length > 0);
</script>

<template>
  <div
    v-if="!loading && !hasUrls"
    class="_empty_list empty-team-url-list"
  >
    <img
      :srcset="`
          ${emptyListImage1x} 1x,
          ${emptyListImage2x} 2x,
          ${emptyListImage3x} 3x
        `"
      class="empty-team-url-list__image"
      :src="emptyListImage1x"
      alt="Empty list"
    >
    <h5 class="empty-team-url-list__title">
      {{ __("organization_dashboard.url_list_empty.title") }}
    </h5>
    <p class="empty-team-url-list__message">
      {{ __("organization_dashboard.url_list_empty.message") }}
    </p>
    <TuButton
      class="_shorten_link empty-team-url-list__short-a-link"
      :label="__('organization_dashboard.url_list_empty.button_label')"
      @click="overlay.showCreateFormPopover()"
    />
  </div>
  <div
    v-else
    class="team-url-list"
    data-test-id="teams-url-list dashboard_sidebar_tinyurls"
    :class="{ 'team-url-list--mobile': isMobileView }"
  >
    <UrlListItem
      v-for="(tu, index) in items"
      :key="index"
      :class="`_url _url${tu.hash} team-url-list__item`"
      :index="index"
      :item="tu"
      :selected="selected"
      @url-selected="emit('url-selected')"
      @tag="(name) => emit('tag', name)"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.empty-team-url-list {
  display: flex;
  gap: spacing.$xxs;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  height: 100%;

  background-color: color.$background-overlay;

  &__title {
    font-size: typography.$h5-font-size;
    line-height: typography.$h5-line-height;
    font-weight: typography.$font-weight-bold;
  }

  &__message {
    font-size: typography.$base-font-size;
    line-height: typography.$base-line-height;
    font-weight: typography.$font-weight-medium;
  }

  &__title,
  &__message {
    margin: 0;
  }
}

.team-url-list {
  $self: &;

  display: flex;
  flex-flow: column;
  flex: 1;

  background-color: color.$background-overlay;

  &__item {
    padding: spacing.$xxxs spacing.$s;

    border-bottom: border.$primary;
    border-bottom-color: color.$background-secondary;
  }

  &--mobile {
    #{ $self }__item {
      padding: spacing.$xxxs;
    }
  }
}
</style>
